import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { Badge, Button, Callout, inputClass } from '@/components/ui';
import { adminSettingsQuery } from '@/queries';

/**
 * Settings. The things the business owns.
 *
 * Until now there was no such screen, and nothing in the application could
 * write `tenant_config`, `companies`, `brands` or `storefronts`. The accent
 * colour sat at #111111 from 26 August, a placeholder somebody invented,
 * because there was no way to correct it without a developer.
 *
 * The page is split by who cares about each part rather than by which table it
 * came from. A person changing the GST rate and a person changing the logo are
 * doing different jobs, and the table they land in is our problem, not theirs.
 *
 * Settings that already have their own screen are LINKED rather than copied
 * here. Two places to change a rate is one place too many.
 */

const GROUPS: Array<{ title: string; blurb: string; keys: string[] }> = [
  {
    title: 'Brand',
    blurb: 'What customers see. Every screen, every email, every document.',
    keys: ['brand.name', 'brand.domain', 'brand.email_from', 'brand.logo_url', 'brand.accent_hex'],
  },
  {
    title: 'The business',
    blurb: 'Who the Australian Taxation Office invoices. This goes on documents.',
    keys: [],
  },
  {
    title: 'Operations',
    blurb: 'Numbers the whole system works from.',
    keys: ['group.name', 'group.currency', 'group.timezone', 'group.gst_rate', 'ops.lead_days'],
  },
  {
    // Spec 015 FR-1520. A warning on the order panel, never a block.
    title: 'Margin targets',
    blurb: 'An order line under its target shows an amber mark on the order. Nothing is blocked.',
    keys: ['ops.margin_target_stocked', 'ops.margin_target_bought'],
  },
];

/** Settings that live on their own screen, because they are a job not a field. */
const ELSEWHERE: Array<{ label: string; href: string; what: string }> = [
  { label: 'Decoration pricing', href: '/admin/methods', what: 'Rates per method, and which methods are offered' },
  { label: 'Xero', href: '/admin/xero', what: 'Account codes, tax treatment and invoice terms' },
  { label: 'Shopify', href: '/admin/integrations', what: 'The bridge, and unmatched variants' },
  { label: 'Permissions', href: '/admin/permissions', what: 'What each role may see and change' },
  { label: 'Staff', href: '/admin/people', what: 'Who has an account, and their role' },
  { label: 'Locations', href: '/admin/locations', what: 'Zones, bays and tubs' },
  { label: 'Machines', href: '/admin/machines', what: 'The presses and their types' },
  { label: 'Suppliers', href: '/admin/suppliers', what: 'Who you buy from, and lead times' },
];

const HINT: Record<string, string> = {
  'brand.name': 'The trading name, shown at the top of every screen.',
  'brand.domain': 'Without https.',
  'brand.email_from': 'Must be a verified sender, or email stops going out.',
  'brand.logo_url': 'A path in the application, or a full web address.',
  'brand.accent_hex': 'Six digits after a hash, like #1A2B3C.',
  'group.name': 'The group the brand belongs to. Shown on internal reports only.',
  'group.currency': 'Three letters. AUD.',
  'group.timezone': 'Used for every date on every screen. An IANA zone name, '
    + 'in Region/City form.',
  'group.gst_rate': 'A fraction, not a percentage. Australian GST is 0.10.',
  'ops.lead_days': 'The default promise on a new job, in days.',
  'ops.margin_target_stocked': 'In percent, 0 to 100. For an item that is a Stock line.',
  'ops.margin_target_bought': 'In percent, 0 to 100. For goods bought for one order, and any line '
    + 'that is not a Stock line.',
};

/** The stored value, as something an input can hold. */
function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
}

export default function AdminSettingsPage() {
  const qc = useQueryClient();
  const { company, values, fields } = useSuspenseQuery(adminSettingsQuery).data;

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [firm, setFirm] = useState({
    legal_name: company?.legal_name ?? '',
    trading_name: company?.trading_name ?? '',
    abn: company?.abn ?? '',
  });
  const [said, setSaid] = useState<string | null>(null);

  const label = (key: string) => fields.find((f) => f.key === key)?.label ?? key;
  const current = (key: string) => edits[key] ?? asText(values[key]);
  const changed = Object.keys(edits).length > 0
    || firm.legal_name !== (company?.legal_name ?? '')
    || firm.trading_name !== (company?.trading_name ?? '')
    || firm.abn !== (company?.abn ?? '');

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/settings', {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ values: edits, company: firm }),
      });
      const body = (await res.json().catch(() => null)) as
        | { message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not save it.');
      return body?.message ?? 'Saved.';
    },
    onSuccess: (m) => {
      setSaid(m);
      setEdits({});
      void qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      // The brand shows in the shell, so that has to refetch too.
      void qc.invalidateQueries({ queryKey: ['admin', 'shell'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  const field = (key: string) => (
    <label key={key} className="block">
      <span className="mb-1 block text-[12.5px] font-semibold">{label(key)}</span>
      <span className="flex items-center gap-2">
        {key === 'brand.accent_hex' && (
          <span
            aria-hidden
            className="h-8 w-8 shrink-0 rounded-[6px] border border-line"
            style={{ background: /^#[0-9a-fA-F]{6}$/.test(current(key)) ? current(key) : 'transparent' }}
          />
        )}
        <input
          value={current(key)}
          onChange={(e) => setEdits((x) => ({ ...x, [key]: e.target.value }))}
          className={`${inputClass} w-full ${key.includes('hex') || key.includes('rate') ? 'font-[family-name:var(--font-mono)]' : ''}`}
        />
      </span>
      <span className="mt-1 block text-[12px] text-muted">{HINT[key]}</span>
    </label>
  );

  return (
    <div>
      <div className="mb-[14px]">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Settings</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          What the business is called, what it charges tax at, and where the
          rest of the settings live.
        </p>
      </div>

      {/* R-239. The colour has been a placeholder since the day it was seeded,
          because nothing could change it. Say so rather than let it look
          deliberate. */}
      {asText(values['brand.accent_hex']) === '#111111' && (
        <Callout tone="warn" title="The accent colour is still a placeholder" className="mb-5">
          <code className="font-[family-name:var(--font-mono)]">#111111</code> was
          invented when the brand was seeded, not taken from your brand guide.
          Set the real one below.
        </Callout>
      )}

      <div className="space-y-8">
        {GROUPS.map((g) => (
          <section key={g.title}>
            <h2 className="text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
              {g.title}
            </h2>
            <p className="mb-3 mt-1 text-[12.5px] text-muted">{g.blurb}</p>

            <div className="rounded-[10px] border border-line bg-panel p-4">
              {g.title === 'The business' ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-[12.5px] font-semibold">Legal name</span>
                    <input
                      value={firm.legal_name}
                      onChange={(e) => setFirm((f) => ({ ...f, legal_name: e.target.value }))}
                      className={`${inputClass} w-full`}
                    />
                    <span className="mt-1 block text-[12px] text-muted">
                      The entity that invoices. This must match Xero.
                    </span>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[12.5px] font-semibold">ABN</span>
                    <input
                      value={firm.abn}
                      onChange={(e) => setFirm((f) => ({ ...f, abn: e.target.value }))}
                      className={`${inputClass} w-full font-[family-name:var(--font-mono)]`}
                    />
                    <span className="mt-1 block text-[12px] text-muted">
                      Eleven digits. Checked against Xero on every settings pull.
                    </span>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[12.5px] font-semibold">Trading name</span>
                    <input
                      value={firm.trading_name}
                      onChange={(e) => setFirm((f) => ({ ...f, trading_name: e.target.value }))}
                      className={`${inputClass} w-full`}
                    />
                    <span className="mt-1 block text-[12px] text-muted">
                      If the business trades under a different name.
                    </span>
                  </label>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">{g.keys.map(field)}</div>
              )}
            </div>
          </section>
        ))}
      </div>

      {said && (
        <p className="mt-5 rounded-[10px] border border-line bg-panel p-3 text-[13px]">{said}</p>
      )}

      <div className="mt-5 flex items-center gap-3">
        <Button type="button" disabled={!changed || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving...' : 'Save settings'}
        </Button>
        {changed && <span className="text-[12.5px] text-muted">Unsaved changes.</span>}
      </div>

      <section className="mt-12">
        <h2 className="text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
          Settings that live on their own screen
        </h2>
        <p className="mb-3 mt-1 max-w-[70ch] text-[12.5px] text-muted">
          These are jobs rather than fields, so they have a screen each. Two
          places to change one rate is one place too many.
        </p>
        <div className="overflow-hidden rounded-[10px] border border-line bg-panel">
          {ELSEWHERE.map((e) => (
            <Link
              key={e.href}
              to={e.href}
              className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-line px-4 py-3 last:border-0 hover:bg-raised"
            >
              <span className="min-w-[160px] text-[14px] font-semibold">{e.label}</span>
              <span className="flex-1 text-[13px] text-muted">{e.what}</span>
            </Link>
          ))}
        </div>
      </section>

      <p className="mt-6 max-w-[70ch] text-[12.5px] text-muted">
        A change here takes effect the next time a screen loads. Prices already
        quoted and orders already placed keep the numbers they were given.
      </p>
    </div>
  );
}
